import { getCurrentUser, isAdmin } from '../auth.js';
import { listConversations, getResults } from '../api.js';
import { DEFAULT_PLAN_ID, getPlanById } from '../plans.js';
import { getDashboardCopy, getCommonCopy, getQaChatCopy } from '../i18n.js';
import { createQaChatPanel } from '../components/QaChatPanel.js';
import { dashboardGlobalContext } from '../components/qa/context.js';
import { registerViewCleanup } from '../utils/viewLifecycle.js';
import { canAccessAppFactory, canAccessLive } from '../versionAccess.js';

const html = String.raw;
const PINNED_STORAGE_KEY = 'anforderungsai:pinned-panels';
const DEFAULT_PINNED = ['live-control', 'upload-flow', 'export-handoff'];
const PANEL_IDS = ['live-control', 'upload-flow', 'export-handoff', 'app-factory', 'pilot-program', 'device-readiness'];
const RECENT_JOBS_LIMIT = 20;
const BACK_KEY = 'anforderungsai:conversation:back';
const formatMeetingDate = (value) => {
  if (!value) return '—';
  const normalized = String(value).trim();
  if (!normalized) return '—';
  const parsed = Date.parse(`${normalized}T00:00:00`);
  if (!Number.isFinite(parsed)) return normalized;
  return new Date(parsed).toLocaleDateString();
};
const toMeetingTimestamp = (value) => {
  if (!value) return 0;
  const normalized = String(value).trim();
  if (!normalized) return 0;
  const parsed = Date.parse(`${normalized}T00:00:00`);
  if (!Number.isFinite(parsed)) return 0;
  return parsed;
};
const normalizeValue = (value) => String(value ?? '').trim();
const collectSpeaker = (value, set) => {
  if (typeof value !== 'string') return;
  const trimmed = value.trim();
  if (!trimmed) return;
  set.add(trimmed);
};
const extractRequirementStats = (payload) => {
  const requirements = payload?.requirements ?? payload?.artifacts?.requirements ?? [];
  const userStories = payload?.userStories ?? payload?.artifacts?.userStories ?? [];
  const speakers = new Set();
  requirements.forEach((item) => {
    collectSpeaker(item?.sourceRange?.speaker, speakers);
    collectSpeaker(item?.speaker, speakers);
  });
  userStories.forEach((item) => {
    collectSpeaker(item?.sourceRange?.speaker, speakers);
    collectSpeaker(item?.speaker, speakers);
  });
  return { count: Array.isArray(requirements) ? requirements.length : 0, speakers: Array.from(speakers.values()) };
};

const formatTemplate = (template, replacements = {}) => {
  if (!template) return '';
  return template.replace(/{{(.*?)}}/g, (_, key) => {
    const trimmed = key.trim();
    const value = replacements[trimmed];
    return value === undefined || value === null ? '' : value;
  });
};

const createPanelLibrary = (panelCopy) => {
  const panels = panelCopy ?? {};
  return [
    {
      id: 'live-control',
      badge: panels.liveControl?.badge ?? '',
      title: panels.liveControl?.title ?? '',
      description: panels.liveControl?.description ?? '',
      render: ({ insights }) => html`
        <p class="muted small">${panels.liveControl?.activeLabel ?? ''}: <strong>${insights.running}</strong></p>
        <div class="row" style="gap:8px;">
          <button type="button" data-nav="#/live">${panels.liveControl?.startButton ?? ''}</button>
          <button type="button" class="ghost-button" data-nav="#/live">${panels.liveControl?.shareButton ?? ''}</button>
        </div>
        <p class="muted tiny">${panels.liveControl?.note ?? ''}</p>
      `,
    },
    {
      id: 'upload-flow',
      badge: panels.uploadFlow?.badge ?? '',
      title: panels.uploadFlow?.title ?? '',
      description: panels.uploadFlow?.description ?? '',
      render: () => {
        const statuses = ['done', 'current'];
        const steps = (panels.uploadFlow?.steps ?? []).map((label, index) => {
          const stepState = statuses[index] ?? '';
          return html`
            <li class="${stepState}">
              <span class="dot"></span>
              <span>${label}</span>
            </li>
          `;
        });
        return html`
          <ol class="status-steps" style="margin:0;">
            ${steps.join('')}
          </ol>
          <p class="muted tiny">${panels.uploadFlow?.note ?? ''}</p>
        `;
      },
    },
    {
      id: 'export-handoff',
      badge: panels.exportHandoff?.badge ?? '',
      title: panels.exportHandoff?.title ?? '',
      description: panels.exportHandoff?.description ?? '',
      render: () => {
        const formats = (panels.exportHandoff?.formats ?? []).map((entry) => html`<li>${entry}</li>`).join('');
        return html`
          <ul class="muted small" style="padding-left:20px; margin:0;">
            ${formats}
          </ul>
          <div class="row" style="gap:8px;">
            <button type="button" class="ghost-button" data-nav="#/jobs">${panels.exportHandoff?.primaryButton ?? ''}</button>
            <button type="button" class="ghost-button" data-nav="#/jobs">${panels.exportHandoff?.secondaryButton ?? ''}</button>
          </div>
        `;
      },
    },
    {
      id: 'app-factory',
      badge: 'NEU',
      title: 'App Factory',
      description: 'Strukturierte Kundengespr\u00e4che mit Leitfaden',
      render: () => html`
        <p class="muted small">Erstelle Apps aus strukturierten Anforderungen</p>
        <div class="row" style="gap:8px;">
          <button type="button" data-nav="#/app-factory">Dashboard</button>
          <button type="button" class="ghost-button" data-nav="#/app-factory/new">Neue Session</button>
        </div>
        <p class="muted tiny">Nur f\u00fcr AppEntwickler & Admin</p>
      `,
    },
    {
      id: 'pilot-program',
      badge: panels.pilotProgram?.badge ?? '',
      title: panels.pilotProgram?.title ?? '',
      description: panels.pilotProgram?.description ?? '',
      render: ({ insights }) => html`
        <p class="muted small">${panels.pilotProgram?.activeLabel ?? ''}: <strong>${insights.customers || 0}</strong></p>
        <p class="muted tiny">${panels.pilotProgram?.note ?? ''}</p>
      `,
    },
    {
      id: 'device-readiness',
      badge: panels.deviceReadiness?.badge ?? '',
      title: panels.deviceReadiness?.title ?? '',
      description: panels.deviceReadiness?.description ?? '',
      render: () => {
        const devices = (panels.deviceReadiness?.devices ?? []).map((device) => html`<span class="pill">${device}</span>`).join('');
        return html`
          <div class="row" style="gap:8px; flex-wrap:wrap;">
            ${devices}
          </div>
          <p class="muted tiny">${panels.deviceReadiness?.note ?? ''}</p>
        `;
      },
    },
  ];
};

const sortByUpdatedAt = (items) =>
  [...items].sort((a, b) => {
    const aTime = a?.updatedAt ? Date.parse(a.updatedAt) : a?.createdAt ? Date.parse(a.createdAt) : 0;
    const bTime = b?.updatedAt ? Date.parse(b.updatedAt) : b?.createdAt ? Date.parse(b.createdAt) : 0;
    return bTime - aTime;
  });

const fetchRecentConversations = async () => {
  const conversations = await listConversations({ limit: RECENT_JOBS_LIMIT });
  return sortByUpdatedAt(conversations);
};

const fetchAllConversations = async () => {
  const conversations = await listConversations({ all: true });
  return sortByUpdatedAt(conversations);
};

const categorizeStatus = (status) => {
  const value = (status ?? '').toString().toUpperCase();
  if (!value) {
    return 'pending';
  }
  if (['RUNNING', 'PROCESSING', 'TRANSCRIBING', 'EXTRACTING'].includes(value)) {
    return 'running';
  }
  if (['COMPLETED', 'READY', 'SUCCESS', 'DONE'].includes(value)) {
    return 'completed';
  }
  if (['FAILED', 'ERROR', 'CANCELED'].includes(value)) {
    return 'error';
  }
  return 'pending';
};

const getStatusLabel = (status, statusLabels = {}) => {
  const value = (status ?? '').toString().toUpperCase();
  if (!value) {
    return statusLabels.PENDING ?? '';
  }
  return statusLabels[value] ?? statusLabels.PENDING ?? value;
};

const renderJobsTable = (jobs, copy, hasJobs, statusLabels) => {
  if (!jobs.length) {
    return `<p class="muted">${hasJobs ? copy.jobs.table.noMatches : copy.jobs.table.empty}</p>`;
  }
  return html`
    <table class="jobs-table">
      <thead>
        <tr>
          <th>${copy.jobs.table.customer}</th>
          <th>${copy.jobs.table.project}</th>
          <th>${copy.jobs.table.meeting ?? 'Meeting'}</th>
          <th>${copy.jobs.table.status}</th>
          <th>${copy.jobs.table.updated}</th>
        </tr>
      </thead>
      <tbody>
        ${jobs
          .map((job) => {
            const timestamp = job.createdAt
              ? new Date(job.createdAt).toLocaleString()
              : job.updatedAt
                ? new Date(job.updatedAt).toLocaleString()
                : '–';
            const customer = job.customerId || job.customer || '–';
            const project = job.projectId || job.project || '–';
            const meeting = formatMeetingDate(job.meetingDate);
            const detailHref = job.conversationId ? `#/conversation/${job.conversationId}` : '';
            const customerCell = detailHref
              ? `<a href="${detailHref}" class="jobs-detail-link">${customer}</a>`
              : customer;
            const projectCell = detailHref
              ? `<a href="${detailHref}" class="jobs-detail-link">${project}</a>`
              : project;
            const status = job.status ?? 'PENDING';
            const badgeClass = categorizeStatus(status);
            const badgeLabel = getStatusLabel(status, statusLabels);
            const errorNote = job.error ? `<div class="muted tiny">${job.error}</div>` : '';
            return html`
              <tr>
                <td>${customerCell}</td>
                <td>${projectCell}</td>
                <td>${meeting}</td>
                <td>
                  <span class="status-badge ${badgeClass}">${badgeLabel}</span>
                  ${errorNote}
                </td>
                <td>${timestamp}</td>
              </tr>
            `;
          })
          .join('')}
      </tbody>
    </table>
  `;
};

const computeInsights = (jobs) => {
  const statusBuckets = jobs.reduce(
    (acc, job) => {
      acc[categorizeStatus(job.status)] += 1;
      return acc;
    },
    { running: 0, completed: 0, error: 0, pending: 0 },
  );
  const customers = new Set();
  const projects = new Set();
  jobs.forEach((job) => {
    if (job.customerId || job.customer) {
      customers.add(job.customerId || job.customer);
    }
    if (job.projectId || job.project) {
      projects.add(job.projectId || job.project);
    }
  });
  return {
    total: jobs.length,
    running: statusBuckets.running,
    completed: statusBuckets.completed,
    failed: statusBuckets.error,
    pending: statusBuckets.pending,
    customers: customers.size,
    projects: projects.size,
  };
};

const sanitizePanelOrder = (panelIds) => {
  const knownIds = new Set(PANEL_IDS);
  const order = [];
  panelIds.forEach((id) => {
    if (knownIds.has(id) && !order.includes(id)) {
      order.push(id);
    }
  });
  return order;
};

const loadPinnedPanels = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return [...DEFAULT_PINNED];
    }
    const raw = window.localStorage.getItem(PINNED_STORAGE_KEY);
    if (!raw) {
      return [...DEFAULT_PINNED];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [...DEFAULT_PINNED];
    }
    const sanitized = sanitizePanelOrder(parsed);
    return sanitized.length ? sanitized : [...DEFAULT_PINNED];
  } catch (error) {
    console.warn('Pinned panel storage unavailable', error);
    return [...DEFAULT_PINNED];
  }
};

const savePinnedPanels = (panels) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    window.localStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(panels));
  } catch (error) {
    console.warn('Pinned panel storage failed', error);
  }
};

const renderPanels = (panelIds, panelLibrary, context, isPinned, panelCopy, showActions = true) =>
  panelIds
    .map((panelId) => panelLibrary.find((panel) => panel.id === panelId))
    .filter(Boolean)
    .map(
      (panel) => html`
        <article class="card panel-card" data-panel="${panel.id}">
          <header>
            <div>
              ${panel.badge ? `<span class="muted tiny" style="letter-spacing:0.1em; text-transform:uppercase;">${panel.badge}</span>` : ''}
              <h4 style="margin:4px 0;">${panel.title}</h4>
              ${panel.description ? `<p class="muted small" style="margin:0;">${panel.description}</p>` : ''}
            </div>
            ${showActions
              ? html`<button type="button" class="ghost-button" data-panel-toggle="${panel.id}">
                  ${isPinned ? panelCopy.unpin : panelCopy.pin}
                </button>`
              : ''}
          </header>
          <div class="stack" style="gap:8px; margin-top:12px;">
            ${panel.render(context)}
          </div>
        </article>
      `,
    )
    .join('');

const buildPortfolioSummary = (jobs) => {
  const customers = new Map();
  const projects = new Map();
  const toTimestamp = (job) => {
    const value = job?.updatedAt || job?.createdAt;
    return value ? Date.parse(value) : 0;
  };
  const toMeeting = (job) => toMeetingTimestamp(job?.meetingDate);

  jobs.forEach((job) => {
    const customerName = job.customerId || job.customer;
    const projectName = job.projectId || job.project;
    const status = job.status ?? 'PENDING';
    const timestamp = toTimestamp(job);
    const jobRef = {
      conversationId: job.conversationId,
      meetingDate: job.meetingDate ?? null,
      createdAt: job.createdAt ?? null,
      updatedAt: job.updatedAt ?? null,
      status,
    };

    if (customerName) {
      const entry = customers.get(customerName) ?? {
        name: customerName,
        projects: new Set(),
        jobRefs: [],
        latest: 0,
        meetingLatest: 0,
      };
      if (projectName) {
        entry.projects.add(projectName);
      }
      entry.jobRefs.push(jobRef);
      entry.latest = Math.max(entry.latest, timestamp);
      entry.meetingLatest = Math.max(entry.meetingLatest, toMeeting(job));
      customers.set(customerName, entry);
    }

    if (projectName) {
      const entry = projects.get(projectName) ?? {
        name: projectName,
        customer: customerName || '',
        status,
        latest: 0,
        meetingLatest: 0,
        jobRefs: [],
      };
      if (timestamp >= entry.latest) {
        entry.status = status;
        entry.customer = customerName || entry.customer;
        entry.latest = timestamp;
      }
      entry.jobRefs.push(jobRef);
      entry.meetingLatest = Math.max(entry.meetingLatest, toMeeting(job));
      projects.set(projectName, entry);
    }
  });

  const customerList = Array.from(customers.values())
    .map((entry) => ({
      name: entry.name,
      projects: entry.projects.size,
      projectNames: Array.from(entry.projects.values()),
      jobRefs: entry.jobRefs,
      latest: entry.latest,
      meetingLatest: entry.meetingLatest,
    }))
    .sort((a, b) => b.latest - a.latest);

  const projectList = Array.from(projects.values())
    .map((entry) => ({ ...entry }))
    .sort((a, b) => b.latest - a.latest);

  return { customers: customerList, projects: projectList };
};

const renderPortfolioSection = (portfolioCopy, summary, statusLabels, selection, statsMap, statsLoading) => {
  const currentSummary = summary ?? { customers: [], projects: [] };
  if (!currentSummary.customers.length && !currentSummary.projects.length) {
    return `<p class="muted">${portfolioCopy.empty}</p>`;
  }

  const activeCustomer = selection?.customer ?? '';
  const activeProject = selection?.project ?? '';
  const toDataValue = (value) => encodeURIComponent(String(value ?? ''));

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    try {
      return new Date(timestamp).toLocaleDateString();
    } catch {
      return '—';
    }
  };

  const formatJobDate = (job) => {
    if (job.meetingDate) return formatMeetingDate(job.meetingDate);
    const value = job.updatedAt || job.createdAt;
    return value ? formatDate(Date.parse(value)) : '—';
  };

  const aggregateRequirementStats = (jobRefs) => {
    let total = 0;
    let missing = false;
    const people = new Set();
    jobRefs.forEach((job) => {
      if (!job?.conversationId) {
        missing = true;
        return;
      }
      const stats = statsMap?.get(job.conversationId);
      if (!stats || !Number.isFinite(stats.count)) {
        missing = true;
      } else {
        total += stats.count;
      }
      (stats?.speakers ?? []).forEach((speaker) => {
        const normalized = normalizeValue(speaker);
        if (normalized) people.add(normalized);
      });
    });
    return { total, missing, people: Array.from(people.values()) };
  };

  const formatCount = (count, missing) => {
    if (missing) {
      return statsLoading ? (portfolioCopy.loadingLabel ?? '…') : '—';
    }
    return String(count);
  };

  const formatNames = (names) => (names && names.length ? names.join(', ') : '—');

  const customerItems = currentSummary.customers
    .map((customer) => {
      const isActive = activeCustomer && activeCustomer === customer.name;
      const jobCount = customer.jobRefs.length;
      const stats = aggregateRequirementStats(customer.jobRefs);
      const peopleLabel = stats.people.length
        ? formatTemplate(portfolioCopy.peopleLabel ?? 'Personen: {{names}}', { names: formatNames(stats.people) })
        : '';
      const projectNames = customer.projectNames ?? [];
      return html`
        <li>
          <button type="button" class="portfolio-row${isActive ? ' active' : ''}" data-portfolio="customer" data-portfolio-value="${toDataValue(customer.name)}">
            <div class="stack portfolio-row__content" style="gap:4px;">
              <strong>${customer.name}</strong>
              <span class="muted tiny">
                ${formatTemplate(portfolioCopy.customerLine, {
                  projects: customer.projects,
                  jobs: jobCount,
                })}
              </span>
              <span class="muted tiny">
                ${formatTemplate(portfolioCopy.projectNamesLabel ?? 'Projekte: {{names}}', {
                  names: formatNames(projectNames),
                })}
              </span>
              <span class="muted tiny">
                ${formatTemplate(portfolioCopy.requirementsTotalLabel ?? 'Anforderungen gesamt: {{count}}', {
                  count: formatCount(stats.total, stats.missing),
                })}
              </span>
              ${peopleLabel ? `<span class="muted tiny">${peopleLabel}</span>` : ''}
            </div>
            <span class="muted tiny portfolio-row__meta">${formatTemplate(portfolioCopy.updatedLabel, { date: formatDate(customer.latest) })}</span>
          </button>
        </li>
      `;
    })
    .join('');

  const projectItems = currentSummary.projects
    .map((project) => {
      const isActive = activeProject && activeProject === project.name;
      const jobRefs = Array.isArray(project.jobRefs) ? project.jobRefs : [];
      const active = jobRefs.some((job) => categorizeStatus(job.status) === 'running');
      const projectStatus = active
        ? (portfolioCopy.projectStatusActive ?? 'aktiv')
        : (portfolioCopy.projectStatusInactive ?? 'inaktiv');
      const jobList = jobRefs
        .slice()
        .sort((a, b) => {
          const aTime = toMeetingTimestamp(a.meetingDate) || Date.parse(a.updatedAt || a.createdAt || 0);
          const bTime = toMeetingTimestamp(b.meetingDate) || Date.parse(b.updatedAt || b.createdAt || 0);
          return bTime - aTime;
        })
        .map((job) => {
          const stats = job.conversationId ? statsMap?.get(job.conversationId) : null;
          const countLabel = formatTemplate(portfolioCopy.requirementsCountLabel ?? '{{count}} Anforderungen', {
            count: formatCount(stats?.count ?? 0, !stats || !Number.isFinite(stats.count)),
          });
          const label = formatTemplate(portfolioCopy.jobLabel ?? 'Meeting {{date}}', { date: formatJobDate(job) });
          return `<li>${label} · ${countLabel}</li>`;
        })
        .join('');

      return html`
        <li>
          <button type="button" class="portfolio-row${isActive ? ' active' : ''}" data-portfolio="project" data-portfolio-value="${toDataValue(project.name)}">
            <div class="stack portfolio-row__content" style="gap:4px;">
              <strong>${project.name}</strong>
              <span class="muted tiny">${project.customer || portfolioCopy.unknownCustomer}</span>
              <span class="muted tiny">${portfolioCopy.projectStatusLabel ?? 'Status'}: ${projectStatus}</span>
              ${jobList ? `<ul class="portfolio-job-list">${jobList}</ul>` : ''}
            </div>
            <div class="stack portfolio-row__meta" style="gap:4px; align-items:flex-end;">
              <span class="muted tiny">${formatTemplate(portfolioCopy.updatedLabel, { date: formatDate(project.latest) })}</span>
            </div>
          </button>
        </li>
      `;
    })
    .join('');

  return html`
    <div class="portfolio-grid">
      <div class="portfolio-column">
        <div class="row" style="justify-content: space-between; align-items:center; gap:8px;">
          <h4 style="margin:0;">${portfolioCopy.customersTitle}</h4>
          <span class="pill muted">${currentSummary.customers.length}</span>
        </div>
        <ul class="portfolio-list">
          ${customerItems || `<li class="muted">${portfolioCopy.emptyCustomers ?? portfolioCopy.empty}</li>`}
        </ul>
      </div>
      <div class="portfolio-column">
        <div class="row" style="justify-content: space-between; align-items:center; gap:8px;">
          <h4 style="margin:0;">${portfolioCopy.projectsTitle}</h4>
          <span class="pill muted">${currentSummary.projects.length}</span>
        </div>
        <ul class="portfolio-list">
          ${projectItems || `<li class="muted">${portfolioCopy.emptyProjects ?? portfolioCopy.empty}</li>`}
        </ul>
      </div>
    </div>
  `;
};

const renderInsightsGrid = (insightsCopy, insights) => html`
  <div class="insight-card">
    <span class="muted tiny">${insightsCopy.cards.total.label}</span>
    <strong>${insights.total}</strong>
    <span class="muted small">${insightsCopy.cards.total.subline}</span>
  </div>
  <div class="insight-card">
    <span class="muted tiny">${insightsCopy.cards.running.label}</span>
    <strong>${insights.running}</strong>
    <span class="muted small">${insightsCopy.cards.running.subline}</span>
  </div>
  <div class="insight-card">
    <span class="muted tiny">${insightsCopy.cards.completed.label}</span>
    <strong>${insights.completed}</strong>
    <span class="muted small">${insightsCopy.cards.completed.subline}</span>
  </div>
  <div class="insight-card">
    <span class="muted tiny">${insightsCopy.cards.customers.label}</span>
    <strong>${insights.customers}/${insights.projects}</strong>
    <span class="muted small">${insightsCopy.cards.customers.subline}</span>
  </div>
`;

export const renderDashboardView = async (container) => {
  const copy = getDashboardCopy();
  const commonCopy = getCommonCopy();
  const qaCopy = getQaChatCopy();
  const panelDefinitions = createPanelLibrary(copy.panels?.entries);
  const user = getCurrentUser();
  if (!user) {
    container.innerHTML = `<div class="card"><p class="muted">${commonCopy.authRequired}</p></div>`;
    return;
  }

  const plan = getPlanById(user.planId) ?? getPlanById(DEFAULT_PLAN_ID);
  const nameParts = [user.profile?.firstName, user.profile?.lastName].filter(Boolean);
  const profileName = nameParts.join(' ').trim();
  const displayName = profileName || user.email || copy.welcome.defaultName;
  const welcomeHeading = formatTemplate(copy.welcome.heading, { name: displayName });
  const isAdminUser = isAdmin(user);
  const hasLiveFeature = canAccessLive(user);
  const hasAppFactoryFeature = canAccessAppFactory(user);

  const state = {
    jobs: [],
    allJobs: [],
    filteredJobs: [],
    insights: computeInsights([]),
    portfolio: buildPortfolioSummary([]),
    searchTerm: '',
    statusFilter: 'all',
    selectedCustomer: '',
    selectedProject: '',
    requirementStats: new Map(),
    statsLoading: false,
    pinnedPanels: loadPinnedPanels(),
    panelLibraryOpen: false,
    hasLoadedJobs: false,
    hasLoadedAllJobs: false,
    loadError: false,
    loadAllError: false,
  };

  const buildQaContext = () => dashboardGlobalContext();

  const qaPanel = createQaChatPanel({
    copy: qaCopy,
    title: qaCopy.title,
    description: qaCopy.description,
    context: buildQaContext(),
  });

  const statusFilterOrder = ['all', 'running', 'completed', 'error'];

  const adminOnlyLabel = copy.panels?.adminOnly ?? 'ADMIN ONLY';
  const portfolioSection = html`
    <section class="card stack" style="gap:16px;">
      <div class="row" style="justify-content: space-between; align-items:center;">
        <div>
          <h3>${copy.portfolio.title}</h3>
          <p class="muted small">${copy.portfolio.subtitle}</p>
        </div>
      </div>
      <div id="dashboard-portfolio"></div>
    </section>
  `;
  const panelsSection = isAdminUser
    ? html`
        <section class="card stack admin-only-surface admin-only-page" style="gap:16px;">
          <div class="row" style="justify-content: space-between; align-items:center; flex-wrap:wrap; gap:8px;">
            <div>
              <h3>${copy.panels.title}</h3>
              <p class="muted small">${copy.panels.subtitle}</p>
            </div>
            <div class="row" style="gap:8px; align-items:center; flex-wrap:wrap;">
              <span class="admin-only-pill">${adminOnlyLabel}</span>
              <button type="button" class="ghost-button" id="panel-library-toggle">${copy.panels.manageOpen}</button>
            </div>
          </div>
          <div id="pinned-panel-grid" class="panel-grid"></div>
          <div id="panel-library" class="panel-grid" style="display:none;"></div>
        </section>
      `
    : '';
  const insightsSection = isAdminUser
    ? html`
        <section class="card stack admin-only-surface admin-only-page" style="gap:16px;">
          <div class="row" style="justify-content: space-between; align-items:center; flex-wrap:wrap; gap:8px;">
            <div>
              <h3>${copy.insights.title}</h3>
              <p class="muted small">${copy.insights.subtitle}</p>
            </div>
            <div class="row" style="gap:8px; align-items:center; flex-wrap:wrap;">
              <span class="admin-only-pill">${adminOnlyLabel}</span>
              <span class="pill">${plan?.name ?? copy.insights.planFallback}</span>
            </div>
          </div>
          <div id="insight-grid" class="insight-grid"></div>
        </section>
      `
    : '';

  container.innerHTML = html`
    <div class="stack dashboard-view" style="gap:24px;">
      <div class="view-header row" style="justify-content: space-between; align-items: flex-start; gap: 1rem;">
        <div class="stack tight">
          <h1>${welcomeHeading}</h1>
          ${copy.welcome.subtitle ? `<p class="muted">${copy.welcome.subtitle}</p>` : ''}
        </div>
      </div>

      <section id="dashboard-qa-chat" class="qa-chat-section"></section>

      ${portfolioSection}

      <section class="card stack" style="gap:16px;">
        <div class="row" style="align-items:flex-start; gap:12px; flex-wrap:wrap;">
          <div class="stack" style="gap:4px;">
            <h3 style="margin:0;">${copy.jobs.title}</h3>
            <span class="muted small" id="job-count-label">${copy.jobs.countLoading}</span>
          </div>
        </div>
        <div class="jobs-toolbar">
          <label style="flex:1; min-width:240px;">
            <span class="muted small">${copy.jobs.searchLabel}</span>
            <input type="text" id="job-search" placeholder="${copy.jobs.searchPlaceholder}" />
          </label>
          <div class="filter-pills" id="job-status-filter">
            ${statusFilterOrder
              .map(
                (code) => html`<button type="button" data-status-filter="${code}" class="${code === 'all' ? 'active' : ''}">
                  ${copy.jobs.filters[code]}
                </button>`,
              )
              .join('')}
          </div>
        </div>
        <div id="dashboard-jobs-list">${copy.jobs.loading}</div>
      </section>

      ${panelsSection}

      ${insightsSection}
    </div>
  `;

  const jobsContainer = container.querySelector('#dashboard-jobs-list');
  const jobCountLabel = container.querySelector('#job-count-label');
  const pinnedContainer = container.querySelector('#pinned-panel-grid');
  const panelLibraryGrid = container.querySelector('#panel-library');
  const panelLibraryToggle = container.querySelector('#panel-library-toggle');
  const insightGrid = container.querySelector('#insight-grid');
  const portfolioContainer = container.querySelector('#dashboard-portfolio');
  const qaContainer = container.querySelector('#dashboard-qa-chat');
  const searchInput = container.querySelector('#job-search');
  const statusButtons = Array.from(container.querySelectorAll('[data-status-filter]'));
  const syncQaContext = () => {
    qaPanel.setContext(buildQaContext());
  };
  const markConversationOrigin = (event) => {
    const link = event.target.closest('a[href^="#/conversation/"]');
    if (!link) return;
    sessionStorage.setItem(BACK_KEY, 'dashboard');
  };
  if (jobsContainer) {
    jobsContainer.addEventListener('click', markConversationOrigin);
  }

  qaPanel.setHost(qaContainer);
  syncQaContext();

  registerViewCleanup(() => {
    qaPanel.destroy();
  });

  const wireNavButtons = () => {
    container.querySelectorAll('[data-nav]').forEach((button) => {
      button.addEventListener('click', (event) => {
        const target = event.currentTarget.getAttribute('data-nav');
        if (target) {
          window.location.hash = target;
        }
      });
    });
  };

  const updatePanelContainers = () => {
    if (!pinnedContainer || !panelLibraryGrid) {
      return;
    }
    const context = { insights: state.insights, jobs: state.jobs };
    const isPanelAllowed = (id) => {
      if (id === 'live-control' && !hasLiveFeature) return false;
      if (id === 'app-factory' && !hasAppFactoryFeature) return false;
      return true;
    };
    const filteredPinnedPanels = state.pinnedPanels.filter((id) => isPanelAllowed(id));
    if (filteredPinnedPanels.length !== state.pinnedPanels.length) {
      state.pinnedPanels = filteredPinnedPanels;
      savePinnedPanels(state.pinnedPanels);
    }
    const pinnedMarkup = renderPanels(filteredPinnedPanels, panelDefinitions, context, true, copy.panels, state.panelLibraryOpen);
    pinnedContainer.innerHTML = pinnedMarkup || `<p class="muted">${copy.panels.pinnedEmpty}</p>`;
    const availablePanels = panelDefinitions
      .map((panel) => panel.id)
      .filter((id) => {
        if (!isPanelAllowed(id)) return false;
        return !state.pinnedPanels.includes(id);
      });
    panelLibraryGrid.innerHTML = availablePanels.length
      ? renderPanels(availablePanels, panelDefinitions, context, false, copy.panels, state.panelLibraryOpen)
      : `<p class="muted">${copy.panels.libraryEmpty}</p>`;
    wireNavButtons();
    container.querySelectorAll('[data-panel-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const panelId = button.getAttribute('data-panel-toggle');
        if (!panelId) return;
        if (state.pinnedPanels.includes(panelId)) {
          state.pinnedPanels = state.pinnedPanels.filter((id) => id !== panelId);
        } else {
          state.pinnedPanels = [...state.pinnedPanels, panelId];
        }
        savePinnedPanels(state.pinnedPanels);
        updatePanelContainers();
      });
    });
  };

  wireNavButtons();

  if (panelLibraryToggle && panelLibraryGrid) {
    panelLibraryToggle.addEventListener('click', () => {
      state.panelLibraryOpen = !state.panelLibraryOpen;
      panelLibraryGrid.style.display = state.panelLibraryOpen ? 'grid' : 'none';
      panelLibraryToggle.textContent = state.panelLibraryOpen ? copy.panels.manageClose : copy.panels.manageOpen;
      updatePanelContainers();
    });
  }

  const applyFilters = () => {
    if (!jobsContainer) {
      return;
    }
    if (state.loadError) {
      return;
    }
    if (!state.hasLoadedJobs) {
      if (jobCountLabel) {
        jobCountLabel.textContent = copy.jobs.countLoading;
      }
      return;
    }
    const sourceJobs =
      state.selectedCustomer || state.selectedProject
        ? state.allJobs
        : state.jobs;
    const term = state.searchTerm.toLowerCase();
    const selectedCustomer = state.selectedCustomer ? state.selectedCustomer.toLowerCase() : '';
    const selectedProject = state.selectedProject ? state.selectedProject.toLowerCase() : '';
    state.filteredJobs = sourceJobs.filter((job) => {
      const statusCategory = categorizeStatus(job.status);
      const statusMatches = state.statusFilter === 'all' || state.statusFilter === statusCategory;
      const haystack = [
        job.customerId,
        job.customer,
        job.projectId,
        job.project,
        job.status,
        job.conversationId,
        job.meetingDate,
      ]
        .filter(Boolean)
        .map((value) => value.toString().toLowerCase());
      const textMatches = !term || haystack.some((value) => value.includes(term));
      const customerMatches =
        !selectedCustomer ||
        [job.customerId, job.customer]
          .filter(Boolean)
          .some((value) => value.toString().toLowerCase() === selectedCustomer);
      const projectMatches =
        !selectedProject ||
        [job.projectId, job.project]
          .filter(Boolean)
          .some((value) => value.toString().toLowerCase() === selectedProject);
      return statusMatches && textMatches && customerMatches && projectMatches;
    });
    jobsContainer.innerHTML = renderJobsTable(state.filteredJobs, copy, state.jobs.length > 0, copy.statusLabels);
    if (jobCountLabel) {
      const totalJobs = state.allJobs.length || state.jobs.length;
      const visibleCount =
        state.selectedCustomer || state.selectedProject
          ? state.filteredJobs.length
          : Math.min(state.filteredJobs.length, RECENT_JOBS_LIMIT);
      jobCountLabel.textContent = state.jobs.length
        ? formatTemplate(copy.jobs.countLabel, {
            visible: visibleCount,
            total: totalJobs,
          })
        : copy.jobs.countEmpty;
    }
    wireNavButtons();
  };

  const loadRequirementStats = async (jobs) => {
    if (!Array.isArray(jobs) || !jobs.length) return;
    const pending = jobs.filter(
      (job) => job?.conversationId && !state.requirementStats.has(job.conversationId),
    );
    if (!pending.length) return;
    state.statsLoading = true;
    renderPortfolio();
    const concurrency = Math.min(4, pending.length);
    let index = 0;
    await Promise.all(
      Array.from({ length: concurrency }, () => (async () => {
        while (index < pending.length) {
          const current = pending[index];
          index += 1;
          if (!current?.conversationId) continue;
          try {
            const payload = await getResults(current.conversationId);
            const stats = extractRequirementStats(payload);
            state.requirementStats.set(current.conversationId, stats);
          } catch (error) {
            console.warn('Failed to load requirements stats', current.conversationId, error);
            state.requirementStats.set(current.conversationId, { count: Number.NaN, speakers: [] });
          }
        }
      })()),
    );
    state.statsLoading = false;
    renderPortfolio();
  };

  if (searchInput) {
    searchInput.addEventListener('input', (event) => {
      state.searchTerm = event.currentTarget.value.trim();
      applyFilters();
    });
  }

  statusButtons.forEach((button) => {
    button.addEventListener('click', () => {
      state.statusFilter = button.getAttribute('data-status-filter') ?? 'all';
      statusButtons.forEach((btn) => btn.classList.toggle('active', btn === button));
      applyFilters();
    });
  });

  if (portfolioContainer) {
    portfolioContainer.addEventListener('click', (event) => {
      const target = event.target.closest('[data-portfolio]');
      if (!target) return;
      const type = target.getAttribute('data-portfolio');
      const rawValue = target.getAttribute('data-portfolio-value');
      const value = rawValue ? decodeURIComponent(rawValue) : '';
      if (!value) return;

      if (type === 'customer') {
        state.selectedCustomer = state.selectedCustomer === value ? '' : value;
        state.selectedProject = '';
      } else if (type === 'project') {
        state.selectedProject = state.selectedProject === value ? '' : value;
        state.selectedCustomer = '';
      }
      renderPortfolio();
      applyFilters();
    });
  }

  const renderInsightsSection = () => {
    if (insightGrid) {
      insightGrid.innerHTML = renderInsightsGrid(copy.insights, state.insights);
    }
  };

  const renderPortfolio = () => {
    syncQaContext();
    if (portfolioContainer) {
      if (!state.hasLoadedAllJobs && !state.loadAllError) {
        portfolioContainer.innerHTML = `<p class="muted">${commonCopy.loading}</p>`;
        return;
      }
      if (state.loadAllError) {
        portfolioContainer.innerHTML = `<p class="muted">${commonCopy.error}</p>`;
        return;
      }
      portfolioContainer.innerHTML = renderPortfolioSection(copy.portfolio, state.portfolio, copy.statusLabels, {
        customer: state.selectedCustomer,
        project: state.selectedProject,
      }, state.requirementStats, state.statsLoading);
    }
  };

  updatePanelContainers();
  renderInsightsSection();
  renderPortfolio();
  applyFilters();

  try {
    const jobs = await fetchRecentConversations();
    state.jobs = jobs;
    state.filteredJobs = jobs;
    state.insights = computeInsights(jobs);
    state.hasLoadedJobs = true;
    state.loadError = false;
    renderInsightsSection();
    updatePanelContainers();
    applyFilters();
  } catch (error) {
    console.error('Failed to load recent jobs', error);
    state.loadError = true;
    if (jobsContainer) {
      jobsContainer.innerHTML = `<p class="muted">${copy.jobs.loadError}</p>`;
    }
    if (jobCountLabel) {
      jobCountLabel.textContent = copy.jobs.loadFailedLabel;
    }
  }

  try {
    const allJobs = await fetchAllConversations();
    state.allJobs = allJobs;
    state.portfolio = buildPortfolioSummary(allJobs);
    state.insights = allJobs.length ? computeInsights(allJobs) : state.insights;
    state.hasLoadedAllJobs = true;
    state.loadAllError = false;
    renderInsightsSection();
    updatePanelContainers();
    renderPortfolio();
    applyFilters();
    loadRequirementStats(allJobs);
  } catch (error) {
    console.error('Failed to load all jobs for portfolio', error);
    state.allJobs = [];
    state.portfolio = buildPortfolioSummary([]);
    state.hasLoadedAllJobs = true;
    state.loadAllError = true;
    renderPortfolio();
  }
};
